import maplibregl, { type Map as MlMap } from "maplibre-gl";
import { analyze, isSimReady, pathGeometry } from "./sim-client";

/**
 * Ambient flow — a thin, always-on layer (no ▶ needed): tiny translucent dots drift
 * down the tailwater path of every dam/gate that is actually releasing (ระบาย >0),
 * and ripples pulse at dams that are full (≥80%) or releasing. CONSTANT dot speed —
 * it shows DIRECTION only, not ETA (that's the ▶ full mode). Everything comes from
 * the latest real discharge/% (never mocked); stale data → faded / no rings.
 * One rAF, two GeoJSON sources, ≤60 dots, under the dam/station/gauge icons.
 * SPEC v0.9.2a. Shares path geometry + discharge with flow-anim (the ▶ mode).
 */
const MAX_DOTS = 60;
const UPDATE_MS = 1000 / 20; // cap worker GeoJSON updates independently of display refresh rate
const LEN_PER_SEC = 0.09;          // polyline-degrees/sec — constant visual speed
const FPS_FLOOR = 24, FPS_WINDOW_MS = 3000;
const DOT_SRC = "amb-dots-src", RIP_SRC = "amb-rip-src", DOT_LAYER = "amb-dots", RIP_LAYER = "amb-rip";

interface Dam { name: string; lat: number; lon: number; percent: number | null; outflowCms: number | null; kind?: string; updatedAt?: string; valueDate?: string; staleValue?: boolean }
interface Src { color: string; coords: [number, number][]; cumLen: number[]; totalLen: number; dots: number; r: number }
interface Rip { lon: number; lat: number; color: string; nRings: number; period: number; w: number }

const pctColor = (p: number | null): string =>
  p == null ? "#8aa0bf" : p < 60 ? "#22e39b" : p < 80 ? "#ffb020" : p <= 100 ? "#ff8a3c" : "#ff3b5c";
const isStale = (d: string | undefined): boolean => {
  if (!d) return false;
  const t = Date.parse(d + "T00:00:00+07:00");
  return Number.isFinite(t) && Date.now() - t > 36 * 3600 * 1000;
};

let map: MlMap | null = null;
let srcs: Src[] = [];
let rips: Rip[] = [];
let raf = 0, running = false, enabled = true, startT = 0, lastTs = 0;
let lastUpdate = 0;
let fps: number[] = [], lowSince = 0;
const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function lenParam(coords: [number, number][]): { cumLen: number[]; totalLen: number } {
  const cumLen = [0]; let total = 0;
  for (let i = 1; i < coords.length; i++) { total += Math.hypot(coords[i]![0] - coords[i - 1]![0], coords[i]![1] - coords[i - 1]![1]); cumLen.push(total); }
  return { cumLen, totalLen: total };
}
function posAtLen(s: Src, L: number): [number, number] {
  L = ((L % s.totalLen) + s.totalLen) % s.totalLen;
  let i = 0; while (i + 1 < s.cumLen.length && s.cumLen[i + 1]! <= L) i++;
  const t0 = s.cumLen[i]!, t1 = s.cumLen[i + 1] ?? t0, f = t1 > t0 ? (L - t0) / (t1 - t0) : 0;
  const a = s.coords[i]!, b = s.coords[i + 1] ?? a;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

/** Ripple strength from REAL state: over→"high"(red,4), ≥80%/big release→"mid"(3), else none. */
function ringOf(percent: number | null, cms: number, barrage: boolean): "high" | "mid" | null {
  if (barrage) return cms >= 1000 ? "high" : cms > 0 ? "mid" : null;
  if (percent == null) return null;
  if (percent >= 100) return "high";
  if (percent >= 80 || cms >= 50) return "mid";
  return null;
}

async function loadData(): Promise<void> {
  const data = (await (await fetch("/data/dams-status.json")).json()) as { dams?: Dam[]; chaophraya?: { lat: number; lon: number; outflow_cms: number } | null; swocDate?: string };
  const dams = [...(data.dams ?? [])];
  const cp = data.chaophraya;
  srcs = []; rips = [];
  type Cand = Dam & { _barrageCms?: number };
  const cand: Cand[] = dams.map((d) => ({ ...d }));
  if (cp && cp.outflow_cms) {
    const c13: Cand = { name: "เจ้าพระยา(C.13)", lat: cp.lat, lon: cp.lon, percent: null, outflowCms: cp.outflow_cms, kind: "diversion", _barrageCms: cp.outflow_cms };
    if (data.swocDate) c13.valueDate = data.swocDate;
    cand.push(c13);
  }
  const seen = new Set<string>();
  for (const d of cand) {
    const key = `${d.lat.toFixed(3)},${d.lon.toFixed(3)}`;
    if (d.kind === "diversion" && !d._barrageCms) continue;           // barrage without discharge → nothing
    const stale = !!d.staleValue || isStale(d.valueDate ?? d.updatedAt);
    const cms = d._barrageCms ?? d.outflowCms ?? 0;
    // flow dots — count ∝ log10(discharge); tiny/zero release → 0 dots
    const dotN = stale ? 0 : Math.max(0, Math.round(Math.log10(Math.max(1, cms)) * 4));
    if (dotN > 0 && isSimReady()) {
      const r = analyze(d.lon, d.lat, 2.5);
      if (r && r.path.length) {
        const g = pathGeometry(r.path);
        if (g && g.coords.length > 1) {
          const { cumLen, totalLen } = lenParam(g.coords);
          if (totalLen > 0) srcs.push({ color: pctColor(d.percent), coords: g.coords, cumLen, totalLen, dots: dotN, r: Math.min(4, 2.2 + Math.log10(Math.max(1, cms)) * 0.4) });
        }
      }
    }
    // ripple — dedupe by position (the เจ้าพระยา dam entry and C.13 share a spot)
    const str = stale ? null : ringOf(d.percent, cms, d.kind === "diversion");
    if (str && !seen.has(key)) {
      seen.add(key);
      rips.push({ lon: d.lon, lat: d.lat, color: str === "high" ? "#ff3b5c" : pctColor(d.percent), nRings: str === "high" ? 4 : 3, period: str === "high" ? 1.4 : 2.2, w: str === "high" ? 3.6 : 2.6 });
    }
  }
  // cap total dots at MAX_DOTS
  const tot = srcs.reduce((a, s) => a + s.dots, 0);
  if (tot > MAX_DOTS) { const f = MAX_DOTS / tot; for (const s of srcs) s.dots = Math.max(1, Math.round(s.dots * f)); }
}

function dotsFC(t: number): GeoJSON.FeatureCollection {
  const feats: GeoJSON.Feature[] = [];
  for (const s of srcs) {
    const spacing = s.totalLen / s.dots;
    for (let k = 0; k < s.dots; k++) {
      const p = posAtLen(s, k * spacing + t * LEN_PER_SEC);
      feats.push({ type: "Feature", geometry: { type: "Point", coordinates: p }, properties: { color: s.color, r: s.r } });
    }
  }
  return { type: "FeatureCollection", features: feats };
}
function ripFC(t: number): GeoJSON.FeatureCollection {
  const feats: GeoJSON.Feature[] = [];
  for (const rp of rips) for (let i = 0; i < rp.nRings; i++) {
    const phase = reduced ? 0.55 : (t / rp.period + i / rp.nRings) % 1;
    feats.push({ type: "Feature", geometry: { type: "Point", coordinates: [rp.lon, rp.lat] }, properties: { phase, color: rp.color, w: rp.w } });
    if (reduced) break; // one static ring in reduced-motion
  }
  return { type: "FeatureCollection", features: feats };
}

function addLayers(): void {
  if (!map || map.getSource(DOT_SRC)) return;
  const before = ["tw-over", "dams-layer", "bkk-gauges"].find((id) => map!.getLayer(id));
  map.addSource(RIP_SRC, { type: "geojson", data: ripFC(0) });
  map.addLayer({ id: RIP_LAYER, type: "circle", source: RIP_SRC, paint: {
    // zoom interpolate must be TOP-LEVEL — put phase (a data expr) inside each stop
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, ["*", ["get", "phase"], 36], 11, ["*", ["get", "phase"], 72]],
    "circle-color": "rgba(0,0,0,0)", "circle-stroke-width": ["get", "w"], "circle-stroke-color": ["get", "color"],
    "circle-stroke-opacity": ["*", ["-", 1, ["get", "phase"]], 0.85] } }, before);
  map.addSource(DOT_SRC, { type: "geojson", data: dotsFC(0) });
  map.addLayer({ id: DOT_LAYER, type: "circle", source: DOT_SRC, paint: {
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, ["get", "r"], 11, ["*", ["get", "r"], 2]],
    "circle-color": ["get", "color"], "circle-opacity": 0.5, "circle-blur": 0.35 } }, before);
}
function setVisible(on: boolean): void {
  for (const id of [DOT_LAYER, RIP_LAYER]) if (map?.getLayer(id) && map.getLayoutProperty(id, "visibility") !== (on ? "visible" : "none")) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}

function frame(ts: number): void {
  if (!running || !map) return;
  // pause visuals while the ▶ full animation is on
  if (document.body.classList.contains("fa-active")) { setVisible(false); lastTs = ts; raf = requestAnimationFrame(frame); return; }
  setVisible(true);
  // Give gestures/camera animation priority over decorative worker updates.
  if (!map.isMoving() && ts - lastUpdate >= UPDATE_MS) {
    lastUpdate = ts;
    const t = (ts - startT) / 1000;
    (map.getSource(DOT_SRC) as maplibregl.GeoJSONSource | undefined)?.setData(dotsFC(t));
    (map.getSource(RIP_SRC) as maplibregl.GeoJSONSource | undefined)?.setData(ripFC(t));
  }
  const dt = ts - lastTs; lastTs = ts;
  if (dt > 0) {
    fps.push(1000 / dt); if (fps.length > 90) fps.shift();
    const avg = fps.reduce((a, b) => a + b, 0) / fps.length;
    if (avg < FPS_FLOOR && fps.length > 30) { if (!lowSince) lowSince = ts; else if (ts - lowSince > FPS_WINDOW_MS) { stop(); return; } }
    else lowSince = 0;
  }
  raf = requestAnimationFrame(frame);
}

function play(): void {
  if (running || !map) return;
  running = true; startT = performance.now(); lastTs = startT; lastUpdate = 0; fps = []; lowSince = 0;
  setVisible(true); raf = requestAnimationFrame(frame);
}
function stop(): void { running = false; cancelAnimationFrame(raf); }

function onVis(): void { if (document.hidden) stop(); else if (enabled && !reduced) play(); }

export async function startAmbient(m: MlMap): Promise<void> {
  map = m;
  try { enabled = localStorage.getItem("fs-ambient") !== "0"; } catch { /* private mode */ }
  await loadData();
  if (!srcs.length && !rips.length) return;
  addLayers();
  document.addEventListener("visibilitychange", onVis);
  if (!enabled) { setVisible(false); return; }
  if (reduced) { setVisible(true); return; } // static frame, no rAF
  play();
}
export function setAmbientEnabled(on: boolean): void {
  enabled = on;
  try { localStorage.setItem("fs-ambient", on ? "1" : "0"); } catch { /* */ }
  if (!map) return;
  if (!on) { stop(); setVisible(false); }
  else if (reduced) setVisible(true);
  else play();
}
export function ambientEnabled(): boolean { return enabled; }
